import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";
import { useDuplicateFlow, useGetFlow } from "@/queries/flows/flows.api";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  TextField,
  useToast,
} from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Schema de validación para el formulario
const duplicateFlowSchema = z.object({
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .max(125, "El nombre no puede exceder 125 caracteres"),
});

type DuplicateFlowFormData = z.infer<typeof duplicateFlowSchema>;

interface Props {
  flowId: string | null;
  isOpen: boolean;
  duplicationType?: "version" | "duplicate";
  onClose: () => void;
}

const DuplicateFlowModal = ({
  flowId,
  isOpen,
  duplicationType,
  onClose,
}: Props) => {
  const client = useQueryClient();
  const { data, isLoading: isLoadingFlowData } = useGetFlow(flowId!, false);
  const { mutateAsync, isPending: isLoading } = useDuplicateFlow(flowId!);
  const { showToast } = useToast();

  const {
    register,
    watch,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<DuplicateFlowFormData>({
    resolver: zodResolver(duplicateFlowSchema),
    defaultValues: {
      name: data?.data.name ? `${data.data.name} (copia)` : "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (data?.data && isOpen && flowId === data.data.id) {
      reset({
        name: data.data.name ? `${data.data.name} (copia)` : "",
      });
    }
  }, [data, isOpen, flowId, reset]);

  const [name] = watch(["name"]);

  const onSubmit = async (data: DuplicateFlowFormData) => {
    try {
      await mutateAsync({
        name: data.name,
        versioning: duplicationType === "version" ? true : false,
      });
      showToast({
        title: "Duplicación en proceso",
        description:
          "El flujo se está duplicando. Podrás ver sus detalles y editarlo una vez de haya duplicado.",
        options: {
          type: "success",
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="info" shape="square">
                <Icon type="fas" name="copy" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
      onClose();
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      if (e.status === 422 && e.response?.data.message.includes("already")) {
        setError("name", {
          type: "manual",
          message: "El nombre del flujo ya existe. Por favor, elige otro.",
        });
      } else {
        setError("name", {
          type: "server",
          message:
            e?.response?.data?.message ||
            e?.message ||
            "Error al duplicar el flujo",
        });
      }
    }
  };

  const handleClose = () => {
    reset({
      name: "",
    });
    clearErrors();
    client.invalidateQueries({
      queryKey: ["admin-flow-detail", flowId],
      exact: true,
    });
    onClose();
  };

  // Validar si el nombre está vacío para deshabilitar el botón
  const isNameEmpty = !name || name.trim() === "";

  return (
    <Modal
      isOpen={isOpen && !!data}
      onClose={handleClose}
      title="Duplicar flujo"
      description="Genera una copia del flujo con un nombre distinto, conservando todas sus configuraciones."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
        id="duplicate-flow-form"
      >
        <TextField
          label="Nombre del flujo"
          required
          placeholder="Nombre del flujo (copia)"
          {...register("name")}
          maxLength={125}
          error={errors.name?.message}
          disabled={isLoadingFlowData}
        />

        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full"
              variant="tertiary"
              onClick={handleClose}
              disabled={isLoading || isLoadingFlowData}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              className="w-full"
              type="submit"
              disabled={
                isLoading || !isValid || isLoadingFlowData || isNameEmpty
              }
              isLoading={isLoading}
            >
              Duplicar flujo
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default DuplicateFlowModal;
